import { useEffect, useMemo, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { nextDay, onCheckinChange } from "../../lib/dates";

export function NewBookingModal({ properties, initial, onClose, onSaved }) {
  const activeProps = useMemo(() => (properties || []).filter((p) => p.active !== false), [properties]);
  const [form, setForm] = useState(() => ({
    apartment_id: initial?.apartment_id || activeProps[0]?.id || "",
    room_type_id: initial?.room_type_id || "",
    checkin: initial?.checkin || "",
    checkout: initial?.checkin ? nextDay(initial.checkin) : "",
    adults: 2, children: 0, pets: false,
    guest_name: "", guest_email: "", guest_phone: "", status: "confirmed",
  }));
  const [quote, setQuote] = useState(null);
  const [price, setPrice] = useState("");
  const [priceEdited, setPriceEdited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedProp = activeProps.find((p) => p.id === form.apartment_id);
  const isHotel = selectedProp?.type === "hotel";

  useEffect(() => {
    // auto-select first room type when a hotel is chosen without a room
    if (isHotel && !form.room_type_id && selectedProp?.room_types?.length) {
      setForm((f) => ({ ...f, room_type_id: selectedProp.room_types[0].id }));
    }
    if (!isHotel && form.room_type_id) setForm((f) => ({ ...f, room_type_id: "" }));
  }, [form.apartment_id]); // eslint-disable-line

  useEffect(() => {
    const run = async () => {
      if (!form.apartment_id || !form.checkin || !form.checkout) { setQuote(null); return; }
      if (isHotel && !form.room_type_id) { setQuote(null); return; }
      setLoading(true);
      try {
        const { data } = await api.post("/admin/bookings/quote", {
          apartment_id: form.apartment_id, room_type_id: form.room_type_id || null,
          checkin: form.checkin, checkout: form.checkout, adults: form.adults, children: form.children,
        });
        setQuote(data);
        if (!priceEdited) setPrice((data.total + (form.pets ? 60 : 0)).toFixed(2));
      } catch (e) { setQuote(null); }
      finally { setLoading(false); }
    };
    run();
  }, [form.apartment_id, form.room_type_id, form.checkin, form.checkout, form.adults, form.children]); // eslint-disable-line

  const save = async () => {
    if (!form.guest_name.trim()) return toast.error("Inserisci il nome dell'ospite");
    if (!form.checkin || !form.checkout) return toast.error("Imposta le date");
    setSaving(true);
    try {
      await api.post("/admin/bookings", { ...form, room_type_id: form.room_type_id || null });
      toast.success("Prenotazione creata");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-fir-deep/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ivory rounded-3xl w-full max-w-lg p-7 my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-booking-form">
        <div className="flex justify-between items-center mb-5">
          <h2 className="font-display text-3xl text-fir">Nuova prenotazione</h2>
          <button onClick={onClose}><X className="text-fir" /></button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="col-span-2"><label className="text-xs uppercase tracking-wide text-fir-deep/60">Struttura</label>
            <select data-testid="new-property" value={form.apartment_id} onChange={(e) => setForm({ ...form, apartment_id: e.target.value, room_type_id: "" })} className="input-alpine w-full mt-1">
              {activeProps.map((p) => <option key={p.id} value={p.id}>{p.type === "hotel" ? "🏨 " : "🏠 "}{p.title}</option>)}
            </select></div>
          {isHotel && (
            <div className="col-span-2"><label className="text-xs uppercase tracking-wide text-fir-deep/60">Tipologia camera</label>
              <select data-testid="new-roomtype" value={form.room_type_id} onChange={(e) => setForm({ ...form, room_type_id: e.target.value })} className="input-alpine w-full mt-1">
                {(selectedProp?.room_types || []).map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
              </select></div>
          )}
          <div><label className="text-xs uppercase tracking-wide text-fir-deep/60">Check-in</label>
            <input type="date" data-testid="new-checkin" value={form.checkin} onChange={(e) => onCheckinChange(e.target.value, form.checkout, setForm)} className="input-alpine w-full mt-1" /></div>
          <div><label className="text-xs uppercase tracking-wide text-fir-deep/60">Check-out</label>
            <input type="date" data-testid="new-checkout" value={form.checkout} min={nextDay(form.checkin)} onChange={(e) => setForm({ ...form, checkout: e.target.value })} className="input-alpine w-full mt-1" /></div>
          <div><label className="text-xs uppercase tracking-wide text-fir-deep/60">Adulti</label>
            <input type="number" min="1" data-testid="new-adults" value={form.adults} onChange={(e) => setForm({ ...form, adults: Number(e.target.value) })} className="input-alpine w-full mt-1" /></div>
          <div><label className="text-xs uppercase tracking-wide text-fir-deep/60">Bambini (&lt;14)</label>
            <input type="number" min="0" data-testid="new-children" value={form.children} onChange={(e) => setForm({ ...form, children: Number(e.target.value) })} className="input-alpine w-full mt-1" /></div>
          <div className="col-span-2"><label className="text-xs uppercase tracking-wide text-fir-deep/60">Nome ospite</label>
            <input data-testid="new-guest-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="input-alpine w-full mt-1" placeholder="Mario Rossi" /></div>
          <div><label className="text-xs uppercase tracking-wide text-fir-deep/60">Email</label>
            <input data-testid="new-guest-email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} className="input-alpine w-full mt-1" placeholder="opzionale" /></div>
          <div><label className="text-xs uppercase tracking-wide text-fir-deep/60">Telefono</label>
            <input data-testid="new-guest-phone" value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} className="input-alpine w-full mt-1" placeholder="opzionale" /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm text-fir-deep/80">
            <input type="checkbox" data-testid="new-pets" checked={form.pets} onChange={(e) => setForm({ ...form, pets: e.target.checked })} /> Animali (+€60)
          </label>
        </div>

        <div className="rounded-2xl border border-wood/40 bg-white p-4 mb-4">
          {loading ? <Loader2 className="animate-spin text-fir mx-auto" /> : quote ? (
            <div>
              <div className="flex justify-between items-center text-sm text-fir-deep/70 mb-2">
                <span>{quote.num_nights} notti{quote.padded_nights > 0 ? ` (fatturate ${quote.billed_nights})` : ""}{form.pets ? " · +€60 animali" : ""}</span>
                <span className="text-xs">Suggerito: €{(quote.total + (form.pets ? 60 : 0)).toFixed(2)}</span>
              </div>
              <label className="text-xs uppercase tracking-wide text-fir-deep/60">Prezzo totale (modificabile)</label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-2xl font-display text-fir">€</span>
                <input data-testid="new-total-input" type="number" min="0" step="0.01" value={price}
                  onChange={(e) => { setPrice(e.target.value); setPriceEdited(true); }}
                  className="input-alpine flex-1 text-2xl font-display" />
              </div>
              {priceEdited && (
                <button type="button" data-testid="new-total-reset" onClick={() => { setPriceEdited(false); setPrice((quote.total + (form.pets ? 60 : 0)).toFixed(2)); }} className="text-xs text-gold mt-2">
                  ↺ Ripristina prezzo suggerito
                </button>
              )}
            </div>
          ) : <p className="text-sm text-fir-deep/50">Seleziona struttura e date valide.</p>}
        </div>

        <button data-testid="create-booking-btn" onClick={save} disabled={saving} className="gold-btn w-full py-3 rounded-xl flex items-center justify-center gap-2">
          {saving ? <Loader2 className="animate-spin" size={18} /> : "Crea prenotazione"}
        </button>
      </div>
    </div>
  );
}
